import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, renderSlot as _renderSlot, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "overflow-x-auto shadow-sm rounded-lg border border-gray-200" }
const _hoisted_2 = { class: "min-w-full divide-y divide-gray-200 text-[10px] md:text-xs" }
const _hoisted_3 = { class: "bg-gray-50" }
const _hoisted_4 = ["onClick"]
const _hoisted_5 = {
  key: 0,
  class: "flex flex-col"
}
const _hoisted_6 = { class: "bg-white divide-y divide-gray-200" }
const _hoisted_7 = ["onClick"]

import { ref, computed } from "vue";
import type { TableHeader, TableRow } from "@/types";

// Props spécifiques au composant ReusableTable
interface Props {
  headers: TableHeader[];
  data: TableRow[];
  sortable?: boolean;
  rowClass?: (row: TableRow) => string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ListProduitTable',
  props: {
    headers: {},
    data: {},
    sortable: { type: Boolean, default: false },
    rowClass: {}
  },
  emits: ["row-click"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

// Variables réactives pour le tri
const sortKey = ref<string>("");
const sortOrder = ref<"asc" | "desc">("asc");

// Fonction de tri
const handleSort = (key: string) => {
  // Vérifier si le tri global est activé ET si cette colonne spécifique est triable
  const header = props.headers.find((h) => h.key === key);
  if (!props.sortable || !header?.sortable) return;

  // Si on clique sur la même colonne, on inverse l'ordre
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === "asc" ? "desc" : "asc";
  } else {
    // Nouvelle colonne, on commence par ordre croissant
    sortKey.value = key;
    sortOrder.value = "asc";
  }
};

// Données triées
const sortedData = computed(() => {
  if (!sortKey.value || !props.sortable) {
    return props.data;
  }

  // Vérifier si la colonne de tri est triable
  const header = props.headers.find((h) => h.key === sortKey.value);
  if (!header?.sortable) {
    return props.data;
  }

  return [...props.data].sort((a, b) => {
    const aValue = a[sortKey.value];
    const bValue = b[sortKey.value];

    // Gestion des valeurs nulles/undefined
    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;

    let comparison = 0;

    // Tri numérique
    if (typeof aValue === "number" && typeof bValue === "number") {
      comparison = aValue - bValue;
    }
    // Tri alphabétique (insensible à la casse)
    else if (typeof aValue === "string" && typeof bValue === "string") {
      comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
    }
    // Tri par défaut
    else {
      comparison = String(aValue).localeCompare(String(bValue));
    }

    // Appliquer l'ordre de tri
    return sortOrder.value === "asc" ? comparison : -comparison;
  });
});

// Événements
const emit = __emit;

const handleRowClick = (row: TableRow) => {
  emit("row-click", row);
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("table", _hoisted_2, [
      _createElementVNode("thead", _hoisted_3, [
        _createElementVNode("tr", null, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.headers, (header, index) => {
            return (_openBlock(), _createElementBlock("th", {
              key: index,
              class: _normalizeClass([
              'pr-2 md:pr-3 pl-1 py-1 md:py-1.5 text-xs md:text-sm font-bold text-black uppercase tracking-wider text-center',
              _ctx.sortable && header.sortable ? 'cursor-pointer' : 'cursor-default',
            ]),
              onClick: ($event: any) => (header.key !== 'checkbox' ? handleSort(header.key) : null)
            }, [
              _createElementVNode("div", {
                class: _normalizeClass([
                'flex items-center',
                (header.headerAlign || header.align) === 'center'
                  ? 'justify-center'
                  : (header.headerAlign || header.align) === 'right'
                  ? 'justify-end'
                  : (header.headerAlign || header.align) === 'left'
                  ? 'justify-start'
                  : 'justify-center',
              ])
              }, [
                _renderSlot(_ctx.$slots, `header-${header.key}`, {
                  header: header,
                  index: index
                }, () => [
                  _createElementVNode("span", null, _toDisplayString(header.label), 1)
                ]),
                (_ctx.sortable && header.sortable && header.key !== 'checkbox')
                  ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                      _createElementVNode("i", {
                        class: _normalizeClass([
                    'fas text-[10px] transition-colors',
                    sortKey.value === header.key && sortOrder.value === 'asc'
                      ? 'fa-sort-up text-blue-600'
                      : 'fa-sort-up text-black',
                  ])
                      }, null, 2),
                      _createElementVNode("i", {
                        class: _normalizeClass([
                    'fas text-[10px] transition-colors -mt-1',
                    sortKey.value === header.key && sortOrder.value === 'desc'
                      ? 'fa-sort-down text-blue-600'
                      : 'fa-sort-down text-black',
                  ])
                      }, null, 2)
                    ]))
                  : _createCommentVNode("", true)
              ], 2)
            ], 10, _hoisted_4))
          }), 128))
        ])
      ]),
      _createElementVNode("tbody", _hoisted_6, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sortedData.value, (row, rowIndex) => {
          return (_openBlock(), _createElementBlock("tr", {
            key: row.id || rowIndex,
            class: _normalizeClass([
            'hover:bg-gray-50 transition-colors duration-150 ease-in-out cursor-pointer border-b border-gray-200',
            _ctx.rowClass ? _ctx.rowClass(row) : '',
          ]),
            onClick: ($event: any) => (handleRowClick(row))
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.headers, (header, colIndex) => {
              return (_openBlock(), _createElementBlock("td", {
                key: colIndex,
                class: _normalizeClass([
              header.cellClass || '',
              'px-1 whitespace-nowrap text-black',
              header.cellClass?.includes('py-') ? '' : 'py-1 md:py-1.5',
            ])
              }, [
                _createElementVNode("div", {
                  class: _normalizeClass([
                'flex items-center',
                header.align === 'center'
                  ? 'justify-center'
                  : header.align === 'right'
                  ? 'justify-end'
                  : header.align === 'left'
                  ? 'justify-start'
                  : 'justify-center',
              ])
                }, [
                  _renderSlot(_ctx.$slots, header.key, {
                    row: row,
                    header: header,
                    rowIndex: rowIndex,
                    colIndex: colIndex
                  }, () => [
                    _createTextVNode(_toDisplayString(row[header.key]), 1)
                  ])
                ], 2)
              ], 2))
            }), 128))
          ], 10, _hoisted_7))
        }), 128))
      ])
    ])
  ]))
}
}

})